import { useCallback, useEffect, useRef, useState } from 'react';
import { useDictionary } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY, FileStatus } from 'Enum';
import { MAX_FILE_SIZE_IN_BYTES, ALLOWED_FILE_TYPES } from 'Config';
import {
  IUploadedFile,
  IDocumentType,
  IDocumentTypeOption,
} from 'Hooks/useDocumentUpload';
import { fileToBase64, getFileExtensionFromName } from 'Utils';

const MIN_FILES_FOR_MODAL = 3;
const FILE_VALIDATION_ERROR_DURATION_MS = 5000;

export interface IDeleteToastItem {
  id: string;
  typeId: string;
  file?: IUploadedFile;
  typeName: string;
  isDocumentType?: boolean;
  documentType?: IDocumentType;
  message: string;
}

interface UseFileUploadHandlersProps {
  documentTypes: IDocumentType[];
  addDocumentType: (type: IDocumentTypeOption) => void;
  addDocumentTypeWithFiles: (
    type: IDocumentTypeOption,
    files: IUploadedFile[],
  ) => void;
  removeDocumentType: (typeId: string) => void;
  addFile: (typeId: string, file: IUploadedFile) => void;
  removeFile: (typeId: string, fileId: string) => void;
  updateFileProgress: (
    typeId: string,
    fileId: string,
    progress: number,
  ) => void;
  updateFileStatus: (
    typeId: string,
    fileId: string,
    status: FileStatus,
    errorMessage?: string,
  ) => void;
}

export const useFileUploadHandlers = ({
  documentTypes,
  addDocumentType,
  addDocumentTypeWithFiles,
  removeDocumentType,
  addFile,
  removeFile,
  updateFileProgress,
  updateFileStatus,
}: UseFileUploadHandlersProps) => {
  const [showModal, setShowModal] = useState(false);
  const [pendingDeleteTypeId, setPendingDeleteTypeId] = useState<string | null>(
    null,
  );
  const [pendingDeleteType, setPendingDeleteType] =
    useState<IDocumentType | null>(null);
  const [toastQueue, setToastQueue] = useState<IDeleteToastItem[]>([]);
  const [fileValidationErrorByTypeId, setFileValidationErrorByTypeId] =
    useState<Record<string, string | null>>({});
  const validationErrorTimeoutByTypeId = useRef<Record<string, number>>({});
  const [previewFile, setPreviewFile] = useState<{
    fileName: string;
    base64: string;
    fileExtension: string;
  } | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const dictionary = useDictionary(Dictionary, DICTIONARY.UniversalRequest);

  const showFileValidationError = useCallback(
    (typeId: string) => {
      if (validationErrorTimeoutByTypeId.current[typeId]) {
        window.clearTimeout(validationErrorTimeoutByTypeId.current[typeId]);
      }
      setFileValidationErrorByTypeId((prev) => ({
        ...prev,
        [typeId]: dictionary.uploadFile.fileInfo,
      }));
      validationErrorTimeoutByTypeId.current[typeId] = window.setTimeout(() => {
        setFileValidationErrorByTypeId((prev) => {
          const next = { ...prev };
          delete next[typeId];
          return next;
        });
        delete validationErrorTimeoutByTypeId.current[typeId];
      }, FILE_VALIDATION_ERROR_DURATION_MS);
    },
    [dictionary.uploadFile.fileInfo],
  );

  useEffect(() => {
    return () => {
      Object.values(validationErrorTimeoutByTypeId.current).forEach(
        (timeoutId) => window.clearTimeout(timeoutId),
      );
      validationErrorTimeoutByTypeId.current = {};
    };
  }, []);

  const animateUploadProgress = useCallback(
    async (typeId: string, fileId: string) => {
      try {
        for (let progress = 0; progress <= 100; progress += 10) {
          await new Promise((resolve) => setTimeout(resolve, 200));
          updateFileProgress(typeId, fileId, progress);
        }
        updateFileStatus(typeId, fileId, FileStatus.SUCCESS);
      } catch (error) {
        updateFileStatus(
          typeId,
          fileId,
          FileStatus.ERROR,
          dictionary.uploadFile.uploadFailed,
        );
      }
    },
    [updateFileProgress, updateFileStatus, dictionary],
  );

  const handleFileSelect = useCallback(
    (typeId: string, fileList: FileList) => {
      const docType = documentTypes.find((dt) => dt.id === typeId);
      if (docType) {
        docType.files
          .filter((f) => f.status === FileStatus.ERROR)
          .forEach((f) => removeFile(typeId, f.id));
      }

      Array.from(fileList).forEach((file) => {
        const fileId = `${Date.now()}-${Math.random()}`;

        const newFile: IUploadedFile = {
          id: fileId,
          name: file.name,
          size: file.size,
          type: file.type,
          uploadProgress: 0,
          status: FileStatus.UPLOADING,
          file,
        };

        if (file.size > MAX_FILE_SIZE_IN_BYTES) {
          showFileValidationError(typeId);
          return;
        }

        const extension = file.name.split('.').pop()?.toLowerCase();
        const allowedExtensions = ALLOWED_FILE_TYPES.split(',').map((ext) =>
          ext.replace('.', '').trim(),
        );

        if (!extension || !allowedExtensions.includes(extension)) {
          showFileValidationError(typeId);
          return;
        }

        addFile(typeId, newFile);
        animateUploadProgress(typeId, fileId);
      });
    },
    [
      addFile,
      dictionary,
      animateUploadProgress,
      documentTypes,
      removeFile,
      showFileValidationError,
    ],
  );

  const handlePreviewFile = useCallback(
    async (typeId: string, fileId: string) => {
      const docType = documentTypes.find((dt) => dt.id === typeId);
      const file = docType?.files.find((f) => f.id === fileId);

      if (file && file.file) {
        const base64 = await fileToBase64(file.file);
        const fileExtension = getFileExtensionFromName(file.name);
        setPreviewFile({
          fileName: file.name,
          base64,
          fileExtension,
        });
        setShowPreviewModal(true);
      }
    },
    [documentTypes],
  );

  const handleClosePreview = useCallback(() => {
    setShowPreviewModal(false);
    setPreviewFile(null);
  }, []);

  const handleAddAnother = useCallback(
    (type: string, label: string) => {
      addDocumentType({ type, label });
    },
    [addDocumentType],
  );

  const handleDelete = useCallback(
    (typeId: string) => {
      const docType = documentTypes.find((dt) => dt.id === typeId);

      if (!docType) return;

      const fileCount = docType.files.length;

      if (fileCount < MIN_FILES_FOR_MODAL) {
        const toastItem: IDeleteToastItem = {
          id: `${Date.now()}-${Math.random()}`,
          typeId,
          typeName: docType.label,
          isDocumentType: true,
          documentType: { ...docType },
          message: dictionary.uploadFile.documentDeleted(docType.label),
        };
        removeDocumentType(typeId);
        setToastQueue((prev) => [...prev, toastItem]);
        return;
      }

      setPendingDeleteTypeId(typeId);
      setPendingDeleteType(docType);
      setShowModal(true);
    },
    [documentTypes, removeDocumentType, dictionary],
  );

  const handleDeleteFile = useCallback(
    (typeId: string, fileId: string) => {
      const docType = documentTypes.find((dt) => dt.id === typeId);
      const file = docType?.files.find((f) => f.id === fileId);

      if (!docType || !file) return;

      removeFile(typeId, fileId);
    },
    [documentTypes, removeFile],
  );

  const handleUndoToast = useCallback(
    (toastId: string) => {
      const toastItem = toastQueue.find((t) => t.id === toastId);
      if (!toastItem) return;

      if (toastItem.isDocumentType && toastItem.documentType) {
        addDocumentTypeWithFiles(
          {
            type: toastItem.documentType.type,
            label: toastItem.documentType.label,
          },
          toastItem.documentType.files,
        );
      } else if (toastItem.file) {
        addFile(toastItem.typeId, toastItem.file);
      }

      setToastQueue((prev) => prev.filter((t) => t.id !== toastId));
    },
    [toastQueue, addFile, addDocumentTypeWithFiles],
  );

  const handleCloseToast = useCallback((toastId: string) => {
    setToastQueue((prev) => prev.filter((t) => t.id !== toastId));
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (pendingDeleteTypeId) {
      removeDocumentType(pendingDeleteTypeId);
      setShowModal(false);
      setPendingDeleteTypeId(null);
      setPendingDeleteType(null);
    }
  }, [pendingDeleteTypeId, removeDocumentType]);

  const handleCancelDelete = useCallback(() => {
    setShowModal(false);
    setPendingDeleteTypeId(null);
    setPendingDeleteType(null);
  }, []);

  return {
    handleFileSelect,
    handlePreviewFile,
    handleClosePreview,
    handleAddAnother,
    handleDelete,
    handleDeleteFile,
    handleUndoToast,
    handleCloseToast,
    handleConfirmDelete,
    handleCancelDelete,
    showModal,
    toastQueue,
    pendingDeleteType,
    showPreviewModal,
    previewFile,
    fileValidationErrorByTypeId,
  };
};
